import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import _, { isEmpty, reduce } from "lodash";
import { v4 as uuid4 } from "uuid";
import * as yup from "yup";
import { useLazyGetPayoutTemplateFullQuery } from "api/baseAPI/payoutTemplates";
import { useCreatePayoutRegistryMutation } from "api/baseAPI/payouts";
import { useErrorNotification } from "utils/notificationWrappers";
import { useUrlQuery } from "utils/url";
import { useTranslateFormErrors } from "utils/useTranslateFormErrors";
import { useStyle } from "hooks/useStyle";
import { IPayoutMethod } from "./payoutMethods";
import { PAYOUT_FIO_LIMIT, splitPayouts } from "./payoutsCreateLogic";

export type PayoutData = {
  customer_account: string;
  amount: string;
  description: string;
  receiver_type: string;
  is_failed_validation: boolean;
  first_name: string;
  middle_name: string;
  last_name: string;
};

export type PayoutFormData = {
  delay: number;
  saveCardsToTemplate: boolean;
  payouts: Record<string, PayoutData>;
  fields: Record<string, string>;
};

export function useBinancePayPayoutForm(payoutMethod: IPayoutMethod) {
  const { t } = useTranslation();
  const style = useStyle();

  const [isSuccess, setIsSuccess] = useState(false);
  const [needSplitPayouts, setNeedSplitPayouts] = useState<boolean>(false);
  const [showAddToTemplateModal, setShowAddToTemplateModal] =
    useState<boolean>(false);
  const [isSplitting, setIsSplitting] = useState<boolean>(false);

  const [createPayoutRegistry] = useCreatePayoutRegistryMutation();

  const initialValues = {
    delay: 0,
    saveCardsToTemplate: false,
    payouts: {
      [uuid4()]: {
        customer_account: "",
        amount: "",
        description: "",
        receiver_type: "BINANCE_ID",
        is_failed_validation: false,
        first_name: "",
        middle_name: "",
        last_name: "",
      },
    },
    fields: {},
  };

  const form = useFormik<PayoutFormData>({
    initialValues,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: yup.object().shape({
      payouts: yup.lazy((value) => {
        if (!isEmpty(value)) {
          const validationObject = {
            customer_account: yup
              .string()
              .when("receiver_type", {
                is: (val: string) => val === "EMAIL",
                then: yup
                  .string()
                  .email(t("Неправильный формат email"))
                  .required(t("Введите email")),
              })
              .when("receiver_type", {
                is: (val: string) => val !== "EMAIL",
                then: yup
                  .string()
                  .required(t("Введите ID"))
                  .when("is_failed_validation", {
                    is: true,
                    then: yup
                      .string()
                      .max(
                        0,
                        t("Получатель отсутствует в системе Binance Pay")
                      ),
                  }),
              }),
            amount: yup
              .number()
              .transform((transformValue, origin) =>
                origin === "" ? null : transformValue
              )
              .nullable()
              .positive(t("Введите положительное число"))
              .test(
                "isValid",
                t("Сумма не меньше {{amount}}", {
                  amount: payoutMethod.commissionsData?.min_amount || 0,
                }),
                (val: number | null | undefined): boolean => {
                  const minAmount =
                    payoutMethod.commissionsData?.min_amount || 0;
                  return !val || !minAmount || val >= minAmount;
                }
              )
              .max(1000000000, t("Сумма не может превышать 1000000000"))
              .required(t("Введите сумму"))
              .typeError(t("Неправильный формат")),
            receiver_type: yup.string().required(t("Выберите")),
            description: yup
              .string()
              .max(100, t("Текст не может быть больше 100 символов")),
            first_name: yup
              .string()
              .when("amount", {
                is: (val: number) =>
                  val >
                    PAYOUT_FIO_LIMIT[
                      payoutMethod.currency?.toLowerCase() || ""
                    ] && style.is_fc,
                then: yup.string().required(t("Введите имя")),
              })
              .matches(/^[a-zA-ZА-яА-Я]+$/, t("Только буквы")),
            last_name: yup
              .string()
              .when("amount", {
                is: (val: number) =>
                  val >
                    PAYOUT_FIO_LIMIT[
                      payoutMethod.currency?.toLowerCase() || ""
                    ] && style.is_fc,
                then: yup.string().required(t("Введите фамилию")),
              })
              .matches(/^[a-zA-ZА-яА-Я]+$/, t("Только буквы")),
          };

          const newEntries = Object.keys(value).reduce(
            (acc, val) => ({
              ...acc,
              [val]: yup.object(validationObject),
            }),
            {}
          );

          return yup.object().shape(newEntries);
        }
        return yup.mixed().notRequired();
      }),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      if (payoutMethod.commissionsData === null) {
        setSubmitting(false);
        return;
      }

      const { max_amount } = payoutMethod.commissionsData;

      setIsSuccess(false);
      setNeedSplitPayouts(false);
      let needStlit = false;

      Object.entries(values.payouts).forEach(([, dataItem]) => {
        if (max_amount && Number(dataItem.amount) > max_amount) {
          setNeedSplitPayouts(true);
          setSubmitting(false);
          needStlit = true;
        }
      });

      const { currency } = payoutMethod;

      if (
        !needStlit &&
        payoutMethod &&
        payoutMethod.serviceId &&
        payoutMethod.pointId &&
        payoutMethod.wallet &&
        currency !== null
      ) {
        createPayoutRegistry({
          account_wallet_id: payoutMethod.wallet.id,
          point_id: payoutMethod.pointId,
          kind: payoutMethod.kind,
          service_id: payoutMethod.serviceId,
          payouts: Object.entries(values.payouts).map(([, payout]) => ({
            customer_account: payout.customer_account,
            amount: Number(payout.amount),
            amount_currency: currency,
            description: payout.description,
            fields: {
              receiver_type: payout.receiver_type,
            },
          })),
        })
          .unwrap()
          .then(() => {
            setIsSuccess(true);
            if (form.values.saveCardsToTemplate) {
              setShowAddToTemplateModal(true);
            } else {
              resetForm({
                values: {
                  ...initialValues,
                },
              });
            }
          })
          .catch((res) => {
            if (
              res.status === 400 &&
              res.data[0] === "Attempt to create duplicate payout"
            ) {
              toast.error(t("Повторная отправка данных"));
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      }
    },
  });

  useTranslateFormErrors(form);

  const addRow = () => {
    form.setFieldValue("payouts", {
      ...form.values.payouts,
      [uuid4()]: {
        customer_account: "",
        amount: "",
        receiver_type: "BINANCE_ID",
        is_failed_validation: false,
        description: "",
        first_name: "",
        middle_name: "",
        last_name: "",
      },
    });
  };

  const deleteRow = (id: string) => {
    form.setFieldValue("payouts", _.omit(form.values.payouts, id));
  };

  const handleSplitPayouts = () => {
    setIsSplitting(true);
    setTimeout(() => {
      if (
        payoutMethod.commissionsData !== null &&
        payoutMethod.commissionsData.max_amount
      ) {
        form
          .setFieldValue(
            "payouts",
            splitPayouts(
              form.values.payouts,
              payoutMethod.commissionsData?.min_amount || 0,
              payoutMethod.commissionsData?.max_amount
            )
          )
          .finally(() => {
            setIsSplitting(false);
            setNeedSplitPayouts(false);
          });
      }
    }, 500);
  };

  return {
    form,
    isSuccess,
    needSplitPayouts,
    setNeedSplitPayouts,
    showAddToTemplateModal,
    setShowAddToTemplateModal,
    addRow,
    deleteRow,
    handleSplitPayouts,
    isSplitting,
  };
}

export const usePayoutsFromTemplate = (params: {
  payoutMethod: IPayoutMethod;
  cb: (data: Record<string, PayoutData>) => void;
}) => {
  const { queryParams, querySetters } = useUrlQuery({
    template_id: "",
  });

  const [getPayoutTemplateFull, getPayoutTemplateFullMeta] =
    useLazyGetPayoutTemplateFullQuery();

  useEffect(() => {
    if (queryParams.template_id) {
      getPayoutTemplateFull({ id: Number(queryParams.template_id) });
    }
  }, [params.payoutMethod, queryParams.template_id]);

  useErrorNotification([getPayoutTemplateFullMeta]);

  useEffect(() => {
    if (!isEmpty(getPayoutTemplateFullMeta.data)) {
      const data = reduce(
        getPayoutTemplateFullMeta?.data?.elements,
        (acc: any, item) => ({
          ...acc,
          [uuid4()]: {
            customer_account: item.customer_account,
            amount: item.amount,
            description: item.description,
            receiver_type: item.fields?.receiver_type,
          },
        }),
        {} as Record<string, PayoutData>
      );
      if (params.payoutMethod.kind === getPayoutTemplateFullMeta.data.kind) {
        params.cb(data);
      }
      querySetters.set_template_id("");
    }
  }, [getPayoutTemplateFullMeta.data]);

  return {
    isLoading: getPayoutTemplateFullMeta.isLoading,
  };
};
